import * as React from 'react'
import { Logger } from 'src/3rds/cube'
import { FCClassContext, classToFComponent } from 'src/3rds/cube-react'

import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Tooltip from '@mui/material/Tooltip'

import HomeIcon from '@mui/icons-material/Home'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import CampaignIcon from '@mui/icons-material/Campaign'
import HubIcon from '@mui/icons-material/Hub'
import InsightsIcon from '@mui/icons-material/Insights'
import BlockIcon from '@mui/icons-material/Block'
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'

import { podeCadastrarEmLote } from 'src/modules/cadastro-lote'
import { podeGerenciarCampanhas } from 'src/modules/campanha'
import { podeGerirGruposManuais } from 'src/modules/grupo-manual'
import { podeVerDashboards } from 'src/modules/dashboards'
import { podeBloquearNumeros } from 'src/modules/bloqueios'
import { podeGerirTemplates } from 'src/modules/templates'
import { podeMandarMensagemDireta } from 'src/modules/mensagem-direta'
import { podeUsarSimulador } from 'src/simulador'

import { makeStyles } from 'tss-react/mui'
import { MainScope } from '../../Main.scopes'

const LOG = Logger.get('DrawerContent')

const useStyles = makeStyles()(() => ({
    listRoot: {
        height: '100%',
        backgroundColor: '#272727ff',
        color: '#ffffffcc'
    },
    settingsItem: {
        margin: '1px 10px',
        borderRadius: '10px',
        bottom: '5px',
        position: 'absolute',
        width: 'calc(100% - 20px)',
        '&:hover': {
            backgroundColor: '#2f2f2f !important'
        },
        '&[data-active="true"]': {
            color: '#6cea48 !important'
        },
        '&[data-active="true"] svg': {
            fill: '#6cea48 !important'
        }
    },
    divider: {
        borderColor: '#ffffff33'
    },
    svg: {
        fill: '#ffffffcc',
        color: '#ffffffcc'
    },
    itemButton: {
        margin: '1px 10px',
        borderRadius: '10px',
        '&:hover': {
            backgroundColor: '#2f2f2f !important'
        },
        '&[data-active="true"]': {
            color: '#6cea48 !important'
        },
        '&[data-active="true"] svg': {
            color: '#6cea48 !important',
            fill: '#6cea48 !important'
        }
    }
}))

export type DrawerContentProps = {
    className?: string
    mainScope: MainScope
}

class DrawerContentClass implements FCClassContext<DrawerContentProps> {
    // :: Fields

    scope!: MainScope

    // :: emissors

    readonly emitOverview = () => this.scope.onOverview().catch(LOG.caught)
    readonly emitCadastroLote = () => this.scope.onCadastroLote().catch(LOG.caught)
    readonly emitCampanha = () => this.scope.onCampanha().catch(LOG.caught)
    readonly emitGrupoManual = () => this.scope.onGrupoManual().catch(LOG.caught)
    readonly emitDashboards = () => this.scope.onDashboards().catch(LOG.caught)
    readonly emitBloqueios = () => this.scope.onBloqueios().catch(LOG.caught)
    readonly emitTemplates = () => this.scope.onTemplates().catch(LOG.caught)
    readonly emitMensagemDireta = () => this.scope.onMensagemDireta().catch(LOG.caught)
    readonly emitSimulador = () => this.scope.onSimulador().catch(LOG.caught)
    readonly emitTheConnection = () => this.scope.onTheConnection().catch(LOG.caught)
    readonly emitTheMap = () => this.scope.onTheMap().catch(LOG.caught)
    readonly emitTheActing = () => this.scope.onTheActing().catch(LOG.caught)
    readonly emitTheConsumerMarket = () => this.scope.onTheConsumerMarket().catch(LOG.caught)
    readonly emitRDStation = () => this.scope.onRdStation().catch(LOG.caught)
    readonly emitUserManager = () => this.scope.onUserManager().catch(LOG.caught)
    readonly emitGroupManager = () => this.scope.onGroupManager().catch(LOG.caught)
    readonly emitConfigManager = () => this.scope.onConfigManager().catch(LOG.caught)

    // :: Methods

    onSyncState({ mainScope }: DrawerContentProps) {
        this.scope = mainScope
    }

    render() {
        const { scope } = this
        const { classes } = useStyles()

        return (
            <List component="nav" className={classes.listRoot}>
                <ListItemButton
                    className={classes.itemButton}
                    onClick={this.emitOverview}
                    data-active={scope.activePage === '/overview'}
                >
                    <Tooltip title="Painel">
                        <ListItemIcon>
                            <HomeIcon className={classes.svg} />
                        </ListItemIcon>
                    </Tooltip>
                    <ListItemText primary="Painel" />
                </ListItemButton>

                {/* Cada perspectiva tem seus papéis (D25); o item aparece para quem vê ao menos uma. */}
                {podeVerDashboards() && (
                    <ListItemButton
                        className={classes.itemButton}
                        onClick={this.emitDashboards}
                        data-active={scope.activePage === '/dashboards'}
                    >
                        <Tooltip title="Dashboards">
                            <ListItemIcon>
                                <InsightsIcon className={classes.svg} />
                            </ListItemIcon>
                        </Tooltip>
                        <ListItemText primary="Dashboards" />
                    </ListItemButton>
                )}

                {/* Simulador do WhatsApp: só no pacote de desenvolvimento (E13) e só para admin. */}
                {podeUsarSimulador() && (
                    <ListItemButton
                        className={classes.itemButton}
                        onClick={this.emitSimulador}
                        data-active={scope.activePage === '/simulador'}
                    >
                        <Tooltip title="Simulador do WhatsApp">
                            <ListItemIcon>
                                <ScienceOutlinedIcon className={classes.svg} />
                            </ListItemIcon>
                        </Tooltip>
                        <ListItemText primary="Simulador do WhatsApp" />
                    </ListItemButton>
                )}

                {/* O cadastro em lote grava negócios no HubSpot de produção: só admin/manager. */}
                {podeCadastrarEmLote() && (
                    <ListItemButton
                        className={classes.itemButton}
                        onClick={this.emitCadastroLote}
                        data-active={scope.activePage === '/cadastroLote'}
                    >
                        <Tooltip title="Cadastro em Lote">
                            <ListItemIcon>
                                <PlaylistAddCheckIcon className={classes.svg} />
                            </ListItemIcon>
                        </Tooltip>
                        <ListItemText primary="Cadastro em Lote" />
                    </ListItemButton>
                )}

                {/* Uma campanha vai para toda a base e não tem desfazer: só admin/manager. */}
                {podeGerenciarCampanhas() && (
                    <ListItemButton
                        className={classes.itemButton}
                        onClick={this.emitCampanha}
                        data-active={scope.activePage === '/campanha'}
                    >
                        <Tooltip title="Campanhas">
                            <ListItemIcon>
                                <CampaignIcon className={classes.svg} />
                            </ListItemIcon>
                        </Tooltip>
                        <ListItemText primary="Campanhas" />
                    </ListItemButton>
                )}

                {/* Os grupos manuais entram no grupo econômico de todos os leads: só admin/manager. */}
                {podeGerirGruposManuais() && (
                    <ListItemButton
                        className={classes.itemButton}
                        onClick={this.emitGrupoManual}
                        data-active={scope.activePage === '/grupoManual'}
                    >
                        <Tooltip title="Grupos manuais">
                            <ListItemIcon>
                                <HubIcon className={classes.svg} />
                            </ListItemIcon>
                        </Tooltip>
                        <ListItemText primary="Grupos manuais" />
                    </ListItemButton>
                )}

                {/* Bloquear corta o WhatsApp da LIA para o número, nos dois sentidos: só admin. */}
                {podeBloquearNumeros() && (
                    <ListItemButton
                        className={classes.itemButton}
                        onClick={this.emitBloqueios}
                        data-active={scope.activePage === '/bloqueios'}
                    >
                        <Tooltip title="Bloqueio de números">
                            <ListItemIcon>
                                <BlockIcon className={classes.svg} />
                            </ListItemIcon>
                        </Tooltip>
                        <ListItemText primary="Bloqueio de números" />
                    </ListItemButton>
                )}

                {/* Um template muda o que a LIA manda a todos os parceiros: só admin (D24). */}
                {podeGerirTemplates() && (
                    <ListItemButton
                        className={classes.itemButton}
                        onClick={this.emitTemplates}
                        data-active={scope.activePage === '/templates'}
                    >
                        <Tooltip title="Templates do WhatsApp">
                            <ListItemIcon>
                                <TextSnippetOutlinedIcon className={classes.svg} />
                            </ListItemIcon>
                        </Tooltip>
                        <ListItemText primary="Templates do WhatsApp" />
                    </ListItemButton>
                )}

                {/* Fala com o parceiro em nome da AG, pelo número da LIA: só admin. */}
                {podeMandarMensagemDireta() && (
                    <ListItemButton
                        className={classes.itemButton}
                        onClick={this.emitMensagemDireta}
                        data-active={scope.activePage === '/mensagemDireta'}
                    >
                        <Tooltip title="Mensagem direta">
                            <ListItemIcon>
                                <ForumOutlinedIcon className={classes.svg} />
                            </ListItemIcon>
                        </Tooltip>
                        <ListItemText primary="Mensagem direta" />
                    </ListItemButton>
                )}
            </List>
        )
    }
}

export default classToFComponent(DrawerContentClass)
